'use client'

import { WifiOff } from 'lucide-react'
import useOnlineStatus from '@/hooks/useOnlineStatus'

/**
 * Global, non-dismissable connectivity indicator.
 *
 * Renders a small toast-styled banner pinned to the bottom-center whenever the
 * browser is offline, and disappears on its own once the connection returns.
 * There is intentionally no close button — the user shouldn't be able to
 * dismiss a state that is still true.
 *
 * Convex's client already pauses queries and queues mutations while offline and
 * replays them on reconnect, so no API calls need to be gated here; this is
 * purely the user-facing signal.
 */
export default function OfflineIndicator() {
  // NOTE: This component is mounted in the root layout OUTSIDE ConvexClientProvider
  // (and therefore outside LocaleProvider/NextIntlClientProvider) so it still shows
  // when Convex/Clerk can't reach the network. It CANNOT use useTranslations — the
  // i18n context isn't in scope here. Keep these strings hardcoded (same rationale
  // as app/global-error.tsx).
  const online = useOnlineStatus()

  if (online) return null

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[110] flex justify-center px-4"
      role="status"
      aria-live="assertive"
    >
      <div
        className="pointer-events-auto flex items-center gap-3 rounded-xl border px-4 py-2.5 shadow-lg"
        style={{
          background: 'var(--color-surface)',
          borderColor: 'var(--color-border)',
        }}
      >
        <div className="flex-shrink-0 rounded-lg bg-[var(--color-orange-muted)] p-1.5">
          <WifiOff className="h-4 w-4 text-[var(--color-orange)]" />
        </div>
        <div className="min-w-0">
          <p
            className="text-sm font-semibold"
            style={{ color: 'var(--color-text-primary)' }}
          >
            No internet connection
          </p>
          <p
            className="text-xs"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Reconnecting automatically when you&rsquo;re back online.
          </p>
        </div>
      </div>
    </div>
  )
}
